import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, LOGO_MARK } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trash2, LogOut, Mail, Loader2, Link2, Copy, Check, CreditCard, Rocket } from "lucide-react";
import { toast } from "sonner";
import { PortfolioManager } from "../components/admin/PortfolioManager";
import { MasteringManager } from "../components/admin/MasteringManager";
import { VoiceLabManager } from "../components/admin/VoiceLabManager";
import { ServicesManager } from "../components/admin/ServicesManager";
import { SiteTextManager } from "../components/admin/SiteTextManager";
import { LayoutManager } from "../components/admin/LayoutManager";

const tabBtn = "data-[state=active]:bg-[#C8A97E] data-[state=active]:text-[#0B0C10]";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [inq, setInq] = useState([]);
  const [payments, setPayments] = useState([]);
  const [linkForm, setLinkForm] = useState({ amount: "", description: "", customer_name: "", customer_email: "" });
  const [genLink, setGenLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [bump, setBump] = useState(0);
  const [drafts, setDrafts] = useState(0);

  useEffect(() => { if (!loading && !user) nav("/admin/login"); }, [loading, user]);
  const loadInq = () => api.get("/admin/inquiries").then((r) => setInq(r.data)).catch(() => {});
  const loadPay = () => api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
  const refreshDrafts = () => api.get("/admin/draft-count").then((r) => setDrafts(r.data.count)).catch(() => {});
  useEffect(() => { if (user) { loadInq(); loadPay(); refreshDrafts(); } }, [user]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#C8A97E]" /></div>;

  const delInq = async (id) => { await api.delete(`/admin/inquiries/${id}`); loadInq(); };
  const doLogout = async () => { await logout(); nav("/admin/login"); };
  const publishAll = async () => {
    const { data } = await api.post("/admin/publish-drafts");
    toast.success(`Published ${data.published} item(s) to your live site`);
    setBump((b) => b + 1); refreshDrafts();
  };

  const buildPayLink = () => {
    const amt = parseFloat(linkForm.amount);
    if (!amt || amt < 1) { toast.error("Enter a valid amount ($1 or more)."); return; }
    const params = new URLSearchParams();
    params.set("amount", String(amt));
    if (linkForm.description.trim()) params.set("desc", linkForm.description.trim());
    if (linkForm.customer_name.trim()) params.set("name", linkForm.customer_name.trim());
    if (linkForm.customer_email.trim()) params.set("email", linkForm.customer_email.trim());
    setGenLink(`${window.location.origin}/pay?${params.toString()}`);
    setCopied(false);
  };
  const copyPayLink = () => {
    navigator.clipboard.writeText(genLink)
      .then(() => { setCopied(true); toast.success("Payment link copied"); setTimeout(() => setCopied(false), 2500); })
      .catch(() => toast.error("Couldn't copy link"));
  };
  const money = (a, c) => `${(c || "usd").toUpperCase() === "USD" ? "$" : ""}${Number(a || 0).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <header className="border-b border-white/10 bg-[#0d0e13]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_MARK} alt="" className="h-9 w-9 object-contain" />
            <span className="font-head font-bold tracking-[0.2em]">E.O.VO ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            {drafts > 0 && (
              <Button onClick={publishAll} data-testid="publish-all-drafts" className="bg-[#3FB950] text-[#0B0C10] hover:bg-[#36a247] font-semibold rounded-sm">
                <Rocket size={15} className="mr-1.5" /> Publish {drafts} Draft{drafts > 1 ? "s" : ""}
              </Button>
            )}
            <Button onClick={doLogout} variant="outline" size="sm" data-testid="admin-logout" className="border-white/15 bg-transparent hover:bg-white/5 text-white rounded-sm"><LogOut size={15} className="mr-1.5" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="inquiries">
          <TabsList className="bg-[#12141A] border border-white/10 mb-8 flex flex-wrap h-auto">
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className={tabBtn}>Inquiries ({inq.length})</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className={tabBtn}>Services</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio" className={tabBtn}>Portfolio</TabsTrigger>
            <TabsTrigger value="voicelab" data-testid="tab-voicelab" className={tabBtn}>Voice FX Lab</TabsTrigger>
            <TabsTrigger value="mastering" data-testid="tab-mastering" className={tabBtn}>Mastering</TabsTrigger>
            <TabsTrigger value="sitetext" data-testid="tab-sitetext" className={tabBtn}>Site Text</TabsTrigger>
            <TabsTrigger value="layout" data-testid="tab-layout" className={tabBtn}>Layout</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments" className={tabBtn}>Payments ({payments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries">
            <div className="space-y-3">
              {inq.length === 0 && <p className="text-[#A1A1AA]">No inquiries yet.</p>}
              {inq.map((q) => (
                <div key={q.id} data-testid={`inquiry-${q.id}`} className="rounded-lg border border-white/10 bg-[#12141A] p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-head font-medium">{q.name}</span>
                        <a href={`mailto:${q.email}`} className="text-xs text-[#C8A97E] flex items-center gap-1"><Mail size={12} /> {q.email}</a>
                        {q.company && <span className="text-xs text-[#A1A1AA]">· {q.company}</span>}
                      </div>
                      <div className="flex gap-3 text-xs text-[#A1A1AA] mt-1">
                        {q.service && <span>Service: {q.service}</span>}
                        {q.budget && <span>Budget: {q.budget}</span>}
                      </div>
                      <p className="text-sm text-[#D4D4D8] mt-2">{q.message}</p>
                      <p className="text-[11px] text-[#6b6b73] mt-2 font-mono">{new Date(q.created_at).toLocaleString()}</p>
                    </div>
                    <Button onClick={() => delInq(q.id)} size="icon" variant="ghost" data-testid={`del-inquiry-${q.id}`} className="text-[#E04F5F] hover:bg-[#E04F5F]/10 shrink-0"><Trash2 size={16} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="services"><ServicesManager bump={bump} onChange={refreshDrafts} /></TabsContent>
          <TabsContent value="portfolio"><PortfolioManager bump={bump} onChange={refreshDrafts} /></TabsContent>
          <TabsContent value="voicelab"><VoiceLabManager bump={bump} onChange={refreshDrafts} /></TabsContent>
          <TabsContent value="mastering"><MasteringManager bump={bump} onChange={refreshDrafts} /></TabsContent>
          <TabsContent value="sitetext"><SiteTextManager /></TabsContent>
          <TabsContent value="layout"><LayoutManager /></TabsContent>

          <TabsContent value="payments">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="rounded-lg border border-white/10 bg-[#12141A] p-6 h-fit">
                <h3 className="font-head text-lg mb-1 flex items-center gap-2"><Link2 size={18} className="text-[#C8A97E]" /> Generate Payment Link</h3>
                <p className="text-xs text-[#A1A1AA] mb-4">Agree a price with the client, then send them a pre-filled secure payment link.</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-[#A1A1AA]">Amount (USD)</Label>
                      <Input type="number" min="1" step="0.01" value={linkForm.amount} onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })} placeholder="250.00" data-testid="paylink-amount" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
                    <div><Label className="text-xs text-[#A1A1AA]">Client name (optional)</Label>
                      <Input value={linkForm.customer_name} onChange={(e) => setLinkForm({ ...linkForm, customer_name: e.target.value })} placeholder="Jane Doe" data-testid="paylink-name" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
                  </div>
                  <div><Label className="text-xs text-[#A1A1AA]">Client email (optional)</Label>
                    <Input type="email" value={linkForm.customer_email} onChange={(e) => setLinkForm({ ...linkForm, customer_email: e.target.value })} placeholder="jane@studio.com" data-testid="paylink-email" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
                  <div><Label className="text-xs text-[#A1A1AA]">What's it for (optional)</Label>
                    <Input value={linkForm.description} onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })} placeholder="Audiobook narration — 45k words (final)" data-testid="paylink-desc" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
                  <Button onClick={buildPayLink} data-testid="paylink-generate" className="w-full bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Generate Link</Button>
                  {genLink && (
                    <div className="rounded-md border border-[#C8A97E]/30 bg-[#C8A97E]/[0.05] p-3" data-testid="paylink-result">
                      <p className="text-[11px] text-[#A1A1AA] mb-1.5">Share this link with your client:</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-[#D4D4D8] break-all flex-1">{genLink}</code>
                        <Button onClick={copyPayLink} size="icon" variant="ghost" data-testid="paylink-copy" className="shrink-0 text-[#C8A97E] hover:bg-[#C8A97E]/10">
                          {copied ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h3 className="font-head text-lg mb-4 flex items-center gap-2"><CreditCard size={18} className="text-[#C8A97E]" /> Transactions</h3>
                <div className="space-y-3">
                  {payments.length === 0 && <p className="text-[#A1A1AA] text-sm">No payments yet. Generated links appear here once a client starts checkout.</p>}
                  {payments.map((p) => {
                    const paid = p.payment_status === "paid";
                    return (
                      <div key={p.id} data-testid={`payment-${p.id}`} className="rounded-lg border border-white/10 bg-[#12141A] p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-head font-semibold text-[#C8A97E]">{money(p.amount, p.currency)}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${paid ? "bg-[#3FB950]/15 text-[#3FB950]" : "bg-white/10 text-[#A1A1AA]"}`} data-testid={`payment-status-${p.id}`}>
                            {paid ? "Paid" : (p.payment_status || "pending")}
                          </span>
                        </div>
                        <div className="text-sm text-[#D4D4D8] mt-1 truncate">{p.label}</div>
                        <div className="text-xs text-[#A1A1AA] mt-0.5 truncate">{p.customer_name || "—"}{p.customer_email ? ` · ${p.customer_email}` : ""}</div>
                        <div className="text-[11px] text-[#6b6b73] mt-1 font-mono">{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
